import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import noteContext from '../Context/notes/noteContext';
import { LogoIcon } from './ui/Logo';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { usePostHog } from 'posthog-js/react';

export default function SignUp() {
  const navigate = useNavigate();
  const { host } = useContext(noteContext);
  const posthog = usePostHog();

  const [credentials, setCredentials] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to handle Google SSO
  const handleGoogleSignUp = async () => {
    try {
      setGoogleLoading(true);
      // Small delay to show loading state before redirect
      await new Promise(resolve => setTimeout(resolve, 300));
      posthog.capture('user_signed_up', { method: 'google' });
      // Redirect to backend Google auth endpoint
      window.location.href = `${host}/api/auth/google`;
    } catch (error) {
      console.error('Google sign-up error:', error);
      setGoogleLoading(false);
    }
  };

  const signup = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      const response = await fetch(`${host}/api/auth/createUser`, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        referrerPolicy: 'no-referrer',
        body: JSON.stringify({
          name: credentials.name,
          email: credentials.email,
          password: credentials.password
        })
      });
      let res = await response.json();
      if (res.success) {
        localStorage.setItem('authtoken', res.authtoken);
        posthog.identify(credentials.email, { email: credentials.email, name: credentials.name });
        posthog.capture('user_signed_up', { method: 'email' });
        navigate('/dashboard');
      } else {
        alert('Email Already Exist');
      }
    } catch (error) {
      document.getElementById('signup_error').innerHTML = error;
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  return (
    <div className='min-h-screen bg-gradient-to-b from-amber-50/50 via-orange-50/30 to-white flex items-center justify-center px-4 py-12'>
      <form className='w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-xl border border-slate-100'>
        <div className='px-8 py-10'>
          {/* Logo - Clickable to go home */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className='flex flex-col items-center justify-center mb-8 w-full hover:opacity-90 transition-opacity'
          >
            <LogoIcon size={80} animated={false} />
            <span className="font-semibold text-xl text-slate-800 mt-2">
              Giftedict
            </span>
          </button>

          {/* Sign Up Title */}
          <h1 className="text-2xl font-bold text-slate-800 text-center mb-8">
            Create Your Account
          </h1>

          {/* Google SSO Button */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-700 font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all mb-6 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <>
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connecting to Google...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-sm text-slate-400 font-medium">or</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>

          {/* Name input field */}
          <div className='mb-4'>
            <label className='block text-slate-700 text-sm font-semibold mb-2' htmlFor='name'>
              Full Name
            </label>
            <input
              type='text'
              name='name'
              id='name'
              onChange={handleOnChange}
              placeholder='Enter your name'
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Email input field */}
          <div className='mb-4'>
            <label className='block text-slate-700 text-sm font-semibold mb-2' htmlFor='email'>
              Email address
            </label>
            <input
              type='email'
              name='email'
              id='email'
              onChange={handleOnChange}
              placeholder='Enter your email'
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Password input field */}
          <div className='mb-6'>
            <label className='block text-slate-700 text-sm font-semibold mb-2' htmlFor='password'>
              Password
            </label>
            <div className='relative w-full'>
              <input
                type={showPassword ? 'text' : 'password'}
                name='password'
                id='password'
                onChange={handleOnChange}
                placeholder='Create a password'
                className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute top-1/2 transform -translate-y-1/2 right-4 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Must be at least 8 characters
            </p>
          </div>

          {/* Sign-up button */}
          <button
            type="submit"
            onClick={signup}
            disabled={loading}
            className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold text-lg shadow-lg shadow-orange-500/30 hover:shadow-xl hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          {/* Terms */}
          <p className="text-xs text-slate-400 text-center mt-4">
            By signing up, you agree to our{' '}
            <a href="#" className="text-orange-600 hover:underline">Terms</a>
            {' '}and{' '}
            <a href="#" className="text-orange-600 hover:underline">Privacy Policy</a>
          </p>

          {/* Sign in link */}
          <p className="text-center mt-6 text-slate-600">
            Already have an account?{' '}
            <Link to="/signin" className="text-orange-600 hover:text-orange-700 font-semibold">
              Sign In
            </Link>
          </p>
        </div>
      </form>

      {/* Display any sign-up errors */}
      <p id='signup_error' className='text-red-500 text-center mt-4 absolute bottom-4'></p>
    </div>
  );
}
